import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEOHead from '../components/SEOHead';
import ImageResizer from '../components/ImageResizer';

const { FiZap, FiShield, FiDownload, FiSettings, FiImage, FiStar, FiUsers, FiTrendingUp, FiLayers } = FiIcons;

function HomePage() {
  const { t } = useTranslation();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const features = [
    {
      icon: FiZap,
      title: t('homepage.features.lightningFast.title'),
      description: t('homepage.features.lightningFast.description')
    },
    {
      icon: FiShield,
      title: t('homepage.features.secure.title'),
      description: t('homepage.features.secure.description')
    },
    {
      icon: FiDownload,
      title: t('homepage.features.free.title'),
      description: t('homepage.features.free.description')
    },
    {
      icon: FiSettings,
      title: t('homepage.features.customDimensions.title'),
      description: t('homepage.features.customDimensions.description')
    },
    {
      icon: FiImage,
      title: t('homepage.features.highQuality.title'),
      description: t('homepage.features.highQuality.description')
    },
    {
      icon: FiStar,
      title: t('homepage.features.multipleFormats.title'),
      description: t('homepage.features.multipleFormats.description')
    }
  ];

  const stats = [
    { number: "1M+", label: t('homepage.stats.imagesProcessed') },
    { number: "50K+", label: t('homepage.stats.happyUsers') },
    { number: "99.9%", label: t('homepage.stats.uptime') },
    { number: "0$", label: t('homepage.stats.costToUse') }
  ];

  const services = [
    {
      title: t('homepage.services.imageResizer.title'),
      description: t('homepage.services.imageResizer.description'),
      icon: FiImage,
      color: "blue",
      link: "/",
      current: true
    },
    {
      title: t('homepage.services.batchResize.title'),
      description: t('homepage.services.batchResize.description'),
      icon: FiLayers,
      color: "blue",
      link: "/batch-resize"
    },
    {
      title: t('homepage.services.imageCompressor.title'),
      description: t('homepage.services.imageCompressor.description'),
      icon: FiZap,
      color: "green",
      link: "/compress"
    },
    {
      title: t('homepage.services.batchCompress.title'),
      description: t('homepage.services.batchCompress.description'),
      icon: FiLayers,
      color: "green",
      link: "/batch-compress"
    }
  ];

  return (
    <>
      <SEOHead
        title={t('seo.homepage.title')}
        description={t('seo.homepage.description')}
        keywords={t('seo.homepage.keywords')}
        url="/"
        image="https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=1200&h=630&fit=crop"
      />
      
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-16"
            >
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                {t('homepage.hero.title')}
                <span className="block text-blue-600">{t('homepage.hero.subtitle')}</span>
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
                {t('homepage.hero.description')}
              </p>
              
              {/* Quick Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <button
                    onClick={() => document.getElementById('image-resizer')?.scrollIntoView({ behavior: 'smooth' })}
                    className="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-700 transition-colors flex items-center"
                  >
                    <SafeIcon icon={FiImage} className="mr-2" />
                    {t('homepage.hero.startResizing')}
                  </button>
                </motion.div>
                <Link to="/compress">
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <button className="bg-green-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-green-700 transition-colors flex items-center">
                      <SafeIcon icon={FiZap} className="mr-2" />
                      {t('homepage.hero.startCompressing')}
                    </button>
                  </motion.div>
                </Link>
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-2xl mx-auto">
                {stats.map((stat, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="text-center"
                  >
                    <div className="text-3xl font-bold text-blue-600">{stat.number}</div>
                    <div className="text-sm text-gray-600">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('homepage.services.title')}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {t('homepage.services.description')}
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link 
                    to={service.link}
                    className={`block bg-white rounded-2xl p-6 hover:shadow-xl transition-all duration-300 h-full ${
                      service.current ? 'ring-2 ring-blue-500 ring-opacity-50' : ''
                    }`}
                  >
                    <div className={`bg-${service.color}-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4`}>
                      <SafeIcon icon={service.icon} className={`text-${service.color}-600 text-xl`} />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      {service.title}
                      {service.current && (
                        <span className="ml-2 text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full">
                          {t('navigation.currentPage')}
                        </span>
                      )}
                    </h3>
                    <p className="text-gray-600">
                      {service.description}
                    </p>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Image Resizer Tool */}
        <section id="image-resizer" className="py-16 px-4 sm:px-6 lg:px-8">
          <ImageResizer />
        </section>

        {/* Features Section */}
        <section id="features" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('homepage.features.title')}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {t('homepage.features.description')}
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-all duration-300"
                >
                  <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                    <SafeIcon icon={feature.icon} className="text-blue-600 text-xl" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Tools Promotion Section */}
        <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12">
              {/* Batch Processing */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="bg-blue-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={FiLayers} className="text-white text-3xl" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('homepage.batchProcessing.title')}
                </h3>
                <p className="text-gray-600 mb-6">
                  {t('homepage.batchProcessing.description')}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Link to="/batch-resize">
                    <button className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors">
                      {t('homepage.batchProcessing.batchResize')}
                    </button>
                  </Link>
                  <Link to="/batch-compress">
                    <button className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors">
                      {t('homepage.batchProcessing.batchCompress')}
                    </button>
                  </Link>
                </div>
              </motion.div>

              {/* Compression Tools */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="bg-green-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={FiZap} className="text-white text-3xl" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('homepage.compressionTools.title')}
                </h3>
                <p className="text-gray-600 mb-6">
                  {t('homepage.compressionTools.description')}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Link to="/compress">
                    <button className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors">
                      {t('homepage.compressionTools.singleCompress')}
                    </button>
                  </Link>
                  <Link to="/batch-compress">
                    <button className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors">
                      {t('homepage.batchProcessing.batchCompress')}
                    </button>
                  </Link>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section id="how-it-works" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('homepage.howItWorks.title')}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {t('homepage.howItWorks.description')}
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  step: "1",
                  title: t('homepage.howItWorks.step1.title'),
                  description: t('homepage.howItWorks.step1.description')
                },
                {
                  step: "2",
                  title: t('homepage.howItWorks.step2.title'),
                  description: t('homepage.howItWorks.step2.description')
                },
                {
                  step: "3",
                  title: t('homepage.howItWorks.step3.title'),
                  description: t('homepage.howItWorks.step3.description')
                }
              ].map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  className="text-center"
                >
                  <div className="bg-blue-600 text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    {step.title}
                  </h3>
                  <p className="text-gray-600">
                    {step.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('homepage.faq.title')}
              </h2>
            </motion.div>

            <div className="space-y-8">
              {[
                {
                  question: t('homepage.faq.q1.question'),
                  answer: t('homepage.faq.q1.answer')
                },
                {
                  question: t('homepage.faq.q2.question'),
                  answer: t('homepage.faq.q2.answer')
                },
                {
                  question: t('homepage.faq.q3.question'),
                  answer: t('homepage.faq.q3.answer')
                },
                {
                  question: t('homepage.faq.q4.question'),
                  answer: t('homepage.faq.q4.answer')
                },
                {
                  question: t('homepage.faq.q5.question'),
                  answer: t('homepage.faq.q5.answer')
                },
                {
                  question: t('homepage.faq.q6.question'),
                  answer: t('homepage.faq.q6.answer')
                }
              ].map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-lg p-6 shadow-sm"
                >
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {faq.question}
                  </h3>
                  <p className="text-gray-600">
                    {faq.answer}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-blue-600">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-white mb-4">
                {t('homepage.cta.title')}
              </h2>
              <p className="text-xl text-blue-100 mb-8">
                {t('homepage.cta.description')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-50 transition-colors"
                >
                  {t('homepage.cta.startProcessing')}
                </motion.button>
                <Link to="/compress">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-800 transition-colors"
                  >
                    {t('homepage.cta.tryCompressor')}
                  </motion.button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
}

export default HomePage;